import React, { useEffect, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getShiftStatus } from './shift_status';
import { formatDateTimeOrdinal } from './date_util';
import { SingleLocationMap } from './single_location_map';

export const ShiftDetail = () => {
  const location = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [shift, setShift] = useState(location.state && location.state.shift ? location.state.shift : null);
  const [loading, setLoading] = useState(!shift);
  const [activeTab, setActiveTab] = useState('Real Time Patrol Monitoring');
  const [hoveredTab, setHoveredTab] = useState(null);
  const [carouselIndex, setCarouselIndex] = useState(null);
  const [activityRecords, setActivityRecords] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [incidents, setIncidents] = useState(null);
  const [incidentFields, setIncidentFields] = useState([]);
  const [incidentsLoading, setIncidentsLoading] = useState(false);
  const [expandedIncidentId, setExpandedIncidentId] = useState(null);
  const fromRequest = location.state && location.state.fromRequest;

  const currentUser = useSelector(state => state.user.currentUser);
  const isAdmin = currentUser && currentUser.role === 'admin';

  const tabs = [
    'Real Time Patrol Monitoring',
    'Scheduled Shift Details',
    'Daily Activity',
    'Incident Reports',
    'Financial Details',
    ...(isAdmin ? ['Admin'] : []),
  ];

  useEffect(() => {
    fetch(`/apishift/${id}`, { credentials: 'include' })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) setShift(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (activeTab !== 'Daily Activity' || activityRecords !== null) return;
    const shiftRecordId = shift && shift[3] ? shift[3].value : null;
    if (!shiftRecordId) return;
    setActivityLoading(true);
    fetch(`/apistatusUpdates/${shiftRecordId}`, { credentials: 'include' })
      .then(res => res.ok ? res.json() : [])
      .then(data => setActivityRecords(Array.isArray(data) ? data : []))
      .catch(() => setActivityRecords([]))
      .finally(() => setActivityLoading(false));
  }, [activeTab, activityRecords, shift]);

  useEffect(() => {
    if (activeTab !== 'Incident Reports' || incidents !== null) return;
    const shiftRecordId = shift && shift[3] ? shift[3].value : null;
    if (!shiftRecordId) return;
    setIncidentsLoading(true);
    fetch(`/apiincidents/${shiftRecordId}`, { credentials: 'include' })
      .then(res => res.ok ? res.json() : { data: [], fields: [] })
      .then(result => {
        setIncidents(Array.isArray(result?.data) ? result.data : []);
        setIncidentFields(Array.isArray(result?.fields) ? result.fields : []);
      })
      .catch(() => { setIncidents([]); setIncidentFields([]); })
      .finally(() => setIncidentsLoading(false));
  }, [activeTab, incidents, shift]);

  useEffect(() => {
    if (carouselIndex === null) return;
    const imageCount = shift ? [shift[1224], shift[1012], shift[1200], shift[1111]].filter(f => f && f.value).length : 0;
    if (imageCount === 0) return;
    const handler = (e) => {
      if (e.key === 'Escape') setCarouselIndex(null);
      else if (e.key === 'ArrowLeft') setCarouselIndex(idx => idx === null ? null : (idx - 1 + imageCount) % imageCount);
      else if (e.key === 'ArrowRight') setCarouselIndex(idx => idx === null ? null : (idx + 1) % imageCount);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [carouselIndex, shift]);

  const formatTime = formatDateTimeOrdinal;

  const isDateValue = (val) => {
    if (!val || typeof val !== 'string') return false;
    return !isNaN(new Date(val).getTime());
  };

  if (loading) return <div className="location"><p>Loading...</p></div>;
  if (!shift) return (
    <div className="location">
      <div className="locationDetailsWrapper">
        <p>Shift not found.</p>
        <button onClick={() => navigate(fromRequest ? `/clientRequest/${fromRequest}` : '/')} className="mainButton">
          ← {fromRequest ? 'Back to Guard Request' : 'Back to Shifts'}
        </button>
      </div>
    </div>
  );

  const recordId = shift[3] ? shift[3].value : '';
  const clientCode = shift[313] ? shift[313].value : '';
  const locationCode = shift[321] ? shift[321].value : '';
  const shiftStatus = shift[366] ? shift[366].value : '';
  const address = shift[898] ? shift[898].value : '';
  const startTime = shift[6] ? formatTime(shift[6].value) : '';
  const endTime = shift[7] ? formatTime(shift[7].value) : '';
  const clockInTime = shift[425] ? formatTime(shift[425].value) : '';
  const clockOutTime = shift[426] ? formatTime(shift[426].value) : '';
  const localStartTime = shift[395] ? formatTime(shift[395].value) : '';
  const localEndTime = shift[396] ? formatTime(shift[396].value) : '';
  const requestType = shift[543] ? shift[543].value : '';
  const armedUnarmed = shift[107] ? shift[107].value : '';
  const requestedBy = shift[333] ? shift[333].value : '';
  const guardType = shift[108] ? shift[108].value : '';

  // Financial fields
  const scheduledHours = shift[467] ? shift[467].value : '';
  const holidayHours = shift[836] ? shift[836].value : '';
  const californiaOvertimeHours = shift[720] ? shift[720].value : '';
  const hourlyRate = shift[507] ? shift[507].value : '';
  const jobTotal = shift[743] ? shift[743].value : '';
  const totalTax = shift[821] ? shift[821].value : '';
  const totalCostPerJob = (jobTotal !== '' || totalTax !== '') ? ((parseFloat(jobTotal) || 0) + (parseFloat(totalTax) || 0)).toFixed(2) : '';

  // Guard information
  const guardName = shift[1113] ? shift[1113].value : '';
  const guardCellPhone = shift[1118] ? shift[1118].value : '';
  const guardIdCardUrl = shift[1224] ? shift[1224].value : '';
  const storefrontImageUrl = shift[1200] ? shift[1200].value : '';
  const armedProofUrl = shift[1111] ? shift[1111].value : '';

  // Clock-in location (field 1103 is an address field — lat is in street1 (1104), lng is in street2 (1105))
  const parseCoord = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = parseFloat(v);
    if (isNaN(n) || n === 0) return null;
    return n;
  };
  let clockInLat = parseCoord(shift[1104] && shift[1104].value);
  let clockInLng = parseCoord(shift[1105] && shift[1105].value);
  // Fallback: 1103 may carry the compound value as an object or "lat, lng" string
  if ((clockInLat === null || clockInLng === null) && shift[1103] && shift[1103].value !== undefined) {
    const v = shift[1103].value;
    if (v && typeof v === 'object') {
      clockInLat = clockInLat ?? parseCoord(v.street1 || v.line1);
      clockInLng = clockInLng ?? parseCoord(v.street2 || v.line2);
    } else if (typeof v === 'string' && v.trim()) {
      const parts = v.split(/[,\n]/).map(s => s.trim()).filter(Boolean);
      if (parts.length >= 2) {
        clockInLat = clockInLat ?? parseCoord(parts[0]);
        clockInLng = clockInLng ?? parseCoord(parts[1]);
      }
    }
  }
  const uniformSelfieUrl = shift[1012] ? shift[1012].value : '';

  const { statusColor, statusText, statusTextColor } = getShiftStatus(shift);

  const carouselImages = [
    guardIdCardUrl && { url: guardIdCardUrl, label: 'Guard ID Card' },
    uniformSelfieUrl && { url: uniformSelfieUrl, label: 'Storefront Image' },
    storefrontImageUrl && { url: storefrontImageUrl, label: 'Selfie Showing Uniform' },
    armedProofUrl && { url: armedProofUrl, label: 'Armed Proof' },
  ].filter(Boolean);

  return (
    <div className="location">
      <div className="locationDetailsWrapper">
        {fromRequest && (
          <button onClick={() => navigate(`/clientRequest/${fromRequest}`)} className="mainButton" style={{ marginBottom: '16px' }}>
            ← Back to Guard Request
          </button>
        )}
        <h2>Shift Details</h2>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', fontWeight: 'bold', fontSize: '14px', color: '#222', marginBottom: '12px' }}>
          <span style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: statusColor, flexShrink: 0 }}></span>
          {statusText}
        </div>
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginBottom: '32px' }}>
          <div>
            <p className="fieldLabel">Location Code</p><p className="field">{locationCode || '—'}</p>
          </div>
          <div style={{ minWidth: '200px' }}>
            <p className="fieldLabel">Address</p><p className="field">{address || '—'}</p>
          </div>
          <div>
            <p className="fieldLabel">Local Start Time</p><p className="field">{localStartTime || '—'}</p>
          </div>
        </div>
        <div style={{ display: 'flex', overflowX: 'auto', marginTop: '8px' }}>
          {tabs.map((tab, idx) => {
            const isActive = activeTab === tab;
            const isHovered = hoveredTab === tab && !isActive;
            return (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                onMouseEnter={() => setHoveredTab(tab)}
                onMouseLeave={() => setHoveredTab(null)}
                style={{
                  background: isActive ? 'white' : (isHovered ? '#e8eaed' : '#f1f3f4'),
                  borderTop: '1px solid #ccc',
                  borderLeft: idx === 0 ? '1px solid #ccc' : 'none',
                  borderRight: '1px solid #ccc',
                  borderBottom: isActive ? 'none' : '1px solid #ccc',
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px',
                  padding: '10px 20px',
                  marginBottom: '-1px',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: isActive ? 'bold' : '500',
                  color: isActive ? '#1a1a1a' : '#5f6368',
                  whiteSpace: 'nowrap',
                  outline: 'none',
                  position: 'relative',
                  zIndex: isActive ? 2 : 1,
                  transition: 'background 120ms ease',
                }}
              >
                {tab}
              </button>
            );
          })}
        </div>

        <div style={{ background: 'white', border: '1px solid #ccc', borderTop: 'none', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px', padding: '24px' }}>

        {activeTab === 'Scheduled Shift Details' && (
          <>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '8px' }}>
              <div style={{ flex: 1 }}>
                <p className="fieldLabel">Shift Start</p><p className="field">{startTime}</p>
              </div>
              <div style={{ flex: 1 }}>
                <p className="fieldLabel">Shift End</p><p className="field">{endTime}</p>
              </div>
              <div style={{ flex: 1 }}>
                <p className="fieldLabel">Local Start Time</p><p className="field">{localStartTime || '—'}</p>
              </div>
              <div style={{ flex: 1 }}>
                <p className="fieldLabel">Local End Time</p><p className="field">{localEndTime || '—'}</p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '8px' }}>
              <div style={{ flex: 1 }}>
                <p className="fieldLabel">Request Type</p><p className="field">{requestType || '—'}</p>
              </div>
              <div style={{ flex: 1 }}>
                <p className="fieldLabel">Armed/Unarmed</p><p className="field">{armedUnarmed || '—'}</p>
              </div>
              <div style={{ flex: 1 }}>
                <p className="fieldLabel">Requested By</p><p className="field">{requestedBy || '—'}</p>
              </div>
              <div style={{ flex: 1 }}>
                <p className="fieldLabel">Guard Type</p><p className="field">{guardType || '—'}</p>
              </div>
            </div>
          </>
        )}

        {activeTab === 'Real Time Patrol Monitoring' && (
          <>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '8px' }}>
              <div style={{ flex: 1 }}>
                <p className="fieldLabel">Clock In Timestamp</p><p className="field">{clockInTime || '—'}</p>
              </div>
              <div style={{ flex: 1 }}>
                <p className="fieldLabel">Clock Out Timestamp</p><p className="field">{clockOutTime || '—'}</p>
              </div>
              <div style={{ flex: 1 }}>
                <p className="fieldLabel">Guard Name</p><p className="field">{guardName || '—'}</p>
              </div>
              <div style={{ flex: 1 }}>
                <p className="fieldLabel">Guard Cell Phone</p><p className="field">{(!guardCellPhone || String(guardCellPhone).trim() === '0') ? '' : guardCellPhone}</p>
              </div>
            </div>
            {(() => {
              const monitoringImages = [
                guardIdCardUrl && { url: guardIdCardUrl, label: 'Guard ID Card' },
                uniformSelfieUrl && { url: uniformSelfieUrl, label: 'Storefront Image' },
                storefrontImageUrl && { url: storefrontImageUrl, label: 'Selfie Showing Uniform' },
                armedProofUrl && { url: armedProofUrl, label: 'Armed Proof' },
              ].filter(Boolean);
              if (monitoringImages.length === 0) return null;
              return (
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '8px' }}>
                  {monitoringImages.map((img, i) => (
                    <div key={img.url} style={{ flex: '0 0 220px' }}>
                      <p className="fieldLabel">{img.label}</p>
                      <img
                        src={img.url}
                        alt={img.label}
                        onClick={() => setCarouselIndex(i)}
                        style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px', marginTop: '8px', cursor: 'zoom-in' }}
                      />
                    </div>
                  ))}
                </div>
              );
            })()}
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '20px' }}>
              <SingleLocationMap title="Job Location" address={address} />
              <SingleLocationMap title="Clock In Location" lat={clockInLat} lng={clockInLng} />
            </div>
            {!clockInTime && !clockOutTime && !guardName && !guardCellPhone && !guardIdCardUrl && !storefrontImageUrl && !armedProofUrl && !uniformSelfieUrl && (
              <p style={{ color: '#666', fontSize: '14px', marginTop: '12px' }}>No real-time monitoring data yet.</p>
            )}
          </>
        )}

        {activeTab === 'Daily Activity' && (
          activityLoading ? (
            <p style={{ color: '#666', fontSize: '14px' }}>Loading activity...</p>
          ) : !activityRecords || activityRecords.length === 0 ? (
            <p style={{ color: '#666', fontSize: '14px' }}>No daily activity records.</p>
          ) : (
            <div className="tableScroll">
              <table id="tableWrapper" className="no-hover">
                <tbody>
                  <tr>
                    <th>Date / Time</th>
                    <th>Guard Name</th>
                    <th>Event Details</th>
                  </tr>
                  {activityRecords.map((r, idx) => (
                    <tr key={r && r[3] ? r[3].value : idx}>
                      <td className="tableCell">{r && r[1] ? formatDateTimeOrdinal(r[1].value) : ''}</td>
                      <td className="tableCell">{r && r[6] ? r[6].value || '' : ''}</td>
                      <td className="tableCell">{r && r[13] ? r[13].value || '' : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {activeTab === 'Incident Reports' && (
          incidentsLoading ? (
            <p style={{ color: '#666', fontSize: '14px' }}>Loading incidents...</p>
          ) : !incidents || incidents.length === 0 ? (
            <p style={{ color: '#666', fontSize: '14px' }}>No incident reports for this shift.</p>
          ) : (() => {
            const hiddenLabels = new Set([
              'add shift',
              'shift records',
              'incident report - file attachment',
              'date created - formatted',
              'incident date/time - formatted',
              'do not hire',
              'shift - client request - client - name',
              'email sent to service',
              'date created',
              'date of incident',
              'injuries reported',
              'severity',
              'recommendations',
              'narrative report',
              'guard name',
              'location',
              'company email',
              'guard email',
              'shift - client request - client location - store location code',
              'did you call the police - text',
              'did police arrive - text',
              'email copy to guard company',
              'email copy to guard',
              'security company name',
            ]);
            const visibleFields = incidentFields.filter(f => !hiddenLabels.has((f.label || '').toLowerCase()));
            const labelOverrides = {
              'shift - vendor bid - vendor': 'Affiliate Name',
              'brief description': 'Incident Details',
              'guard name (text)': 'Guard Name',
            };
            const renderLabel = (f) => labelOverrides[(f.label || '').toLowerCase()] || f.label || `Field ${f.id}`;
            const incidentDateField = incidentFields.find(f => (f.label || '').toLowerCase().includes('incident date'));
            const dateFieldId = incidentDateField ? incidentDateField.id : 1;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {incidents.map((r, idx) => {
                  const recordKey = r && r[3] && r[3].value ? r[3].value : idx;
                  const isExpanded = expandedIncidentId === recordKey;
                  const dateCell = r && r[dateFieldId];
                  const dateVal = dateCell && dateCell.value ? formatDateTimeOrdinal(dateCell.value) : '—';
                  return (
                    <div key={recordKey} style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '16px' }}>
                      <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>Incident #{idx + 1}</h3>
                      <p style={{ margin: '6px 0 12px', color: '#444' }}>Incident Date: {dateVal}</p>
                      <button
                        type="button"
                        className="mainButton"
                        style={{ margin: 0 }}
                        onClick={() => setExpandedIncidentId(isExpanded ? null : recordKey)}
                      >
                        {isExpanded ? 'Hide Incident Details' : 'View Incident Details'}
                      </button>
                      {isExpanded && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginTop: '16px', background: '#fafafa', padding: '16px', borderRadius: '6px' }}>
                          {(() => {
                            const formatIncidentTypes = (val) => {
                              if (Array.isArray(val)) return val.filter(Boolean).join(', ');
                              if (typeof val === 'string') {
                                return val.split(/[;,\n]/).map(s => s.trim()).filter(Boolean).join(', ');
                              }
                              return String(val || '—');
                            };
                            const renderField = (f, fullWidth) => {
                              const cell = r && r[f.id];
                              const rawVal = cell && cell.value !== undefined ? cell.value : '';
                              const label = (f.label || '').toLowerCase();
                              let val = rawVal;
                              if (val && typeof val === 'object' && !Array.isArray(val)) {
                                val = val.url || val.name || JSON.stringify(val);
                              }
                              let display;
                              if (label === 'did you call the police?') {
                                if (rawVal === true || rawVal === 'true' || rawVal === 1 || rawVal === '1') display = 'Yes';
                                else if (rawVal === false || rawVal === 'false' || rawVal === 0 || rawVal === '0') display = 'No';
                                else display = String(val || '—');
                              } else if (label === 'did police arrive?') {
                                if (rawVal === true || rawVal === 'true' || rawVal === 1 || rawVal === '1') display = 'Yes';
                                else if (rawVal === false || rawVal === 'false' || rawVal === 0 || rawVal === '0') display = 'No';
                                else if (rawVal === '' || rawVal === null || rawVal === undefined) display = 'No';
                                else display = String(val);
                              } else if (label === 'incident types' || label === 'incident type') {
                                display = formatIncidentTypes(val);
                              } else {
                                display = String(val || '—');
                              }
                              return (
                                <div key={f.id} style={{ flex: fullWidth ? '1 1 100%' : '1 1 220px', minWidth: fullWidth ? '100%' : '180px' }}>
                                  <p className="fieldLabel">{renderLabel(f)}</p>
                                  <p className="field" style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{display}</p>
                                </div>
                              );
                            };
                            const priority = [
                              'shift - vendor bid - vendor', // Affiliate Name
                              'guard name (text)',           // Guard Name
                              'guard license number',
                              'did you call the police?',
                              'did police arrive?',
                              'police report number',
                              'police officer name',
                              'badge number',
                              'incident types',
                            ];
                            const isBrief = (f) => (f.label || '').toLowerCase() === 'brief description';
                            const labelKey = (f) => (f.label || '').toLowerCase();
                            const priorityFields = priority
                              .map(key => visibleFields.find(f => labelKey(f) === key))
                              .filter(Boolean);
                            const remainingFields = visibleFields.filter(f => !isBrief(f) && !priority.includes(labelKey(f)));
                            const briefField = visibleFields.find(isBrief);
                            return (
                              <>
                                {priorityFields.map(f => renderField(f, false))}
                                {remainingFields.map(f => renderField(f, false))}
                                {briefField && renderField(briefField, true)}
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()
        )}

        {activeTab === 'Financial Details' && (
          <>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '8px' }}>
              <div style={{ flex: 1 }}>
                <p className="fieldLabel">Scheduled Hours</p><p className="field">{scheduledHours === '' ? '0' : scheduledHours}</p>
              </div>
              <div style={{ flex: 1 }}>
                <p className="fieldLabel">Holiday Hours</p><p className="field">{holidayHours === '' || holidayHours == null ? '0' : holidayHours}</p>
              </div>
              <div style={{ flex: 1 }}>
                <p className="fieldLabel">California Overtime Hours</p><p className="field">{californiaOvertimeHours === '' ? '0' : californiaOvertimeHours}</p>
              </div>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <p className="fieldLabel">Hourly Rate</p><p className="field">${hourlyRate === '' ? '0' : hourlyRate}</p>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <p className="fieldLabel">Job Total</p><p className="field">${jobTotal === '' ? '0' : jobTotal}</p>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <p className="fieldLabel">Total Tax</p><p className="field">${totalTax === '' ? '0' : totalTax}</p>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <p className="fieldLabel">Job Total with Tax</p><p className="field">${totalCostPerJob === '' ? '0.00' : totalCostPerJob}</p>
            </div>
          </>
        )}

        {activeTab === 'Admin' && (
          <div style={{ marginBottom: '8px' }}>
            <p className="fieldLabel">Shift Record ID</p><p className="field">{recordId || '—'}</p>
          </div>
        )}
        </div>
      </div>

      {carouselIndex !== null && carouselImages[carouselIndex] && (
        <div
          onClick={() => setCarouselIndex(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: '24px',
          }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setCarouselIndex(null); }}
            aria-label="Close"
            style={{
              position: 'absolute', top: '16px', right: '20px',
              background: 'transparent', border: 'none', color: 'white',
              fontSize: '32px', cursor: 'pointer', lineHeight: 1,
            }}
          >×</button>

          {carouselImages.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setCarouselIndex((carouselIndex - 1 + carouselImages.length) % carouselImages.length); }}
              aria-label="Previous"
              style={{
                position: 'absolute', left: '20px', top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white',
                fontSize: '40px', cursor: 'pointer', padding: '8px 16px', borderRadius: '8px',
              }}
            >‹</button>
          )}

          <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: '90vw', maxHeight: '90vh' }}>
            <img
              src={carouselImages[carouselIndex].url}
              alt={carouselImages[carouselIndex].label}
              style={{ maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: '8px' }}
            />
            <div style={{ color: 'white', marginTop: '12px', fontSize: '15px' }}>
              {carouselImages[carouselIndex].label}
              {carouselImages.length > 1 && (
                <span style={{ color: '#bbb', marginLeft: '8px' }}>
                  ({carouselIndex + 1} of {carouselImages.length})
                </span>
              )}
            </div>
          </div>

          {carouselImages.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setCarouselIndex((carouselIndex + 1) % carouselImages.length); }}
              aria-label="Next"
              style={{
                position: 'absolute', right: '20px', top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white',
                fontSize: '40px', cursor: 'pointer', padding: '8px 16px', borderRadius: '8px',
              }}
            >›</button>
          )}
        </div>
      )}
    </div>
  );
};
